'use client';

import { MultiSelectProvider, useMultiSelect } from '@magicschool/spellbook/components/MultiSelect/MultiSelectProvider';
import type { MultiSelectGroupProps, MultiSelectOptionProps } from '@magicschool/spellbook/components/MultiSelect/slots';
import {
  MultiSelectChipsSlot,
  MultiSelectContentSlot,
  MultiSelectEmptySlot,
  MultiSelectGroupSlot,
  MultiSelectLeadingAddOnSlot,
  MultiSelectLeadingAdornmentSlot,
  MultiSelectOptionSlot,
  MultiSelectSpinnerSlot,
  MultiSelectTrailingAddOnSlot,
  MultiSelectTrailingAdornmentSlot,
  MultiSelectTriggerSlot,
} from '@magicschool/spellbook/components/MultiSelect/slots';
import { Popover } from '@magicschool/spellbook/components/Popover';
import type { SelectProps } from '@magicschool/spellbook/components/Select';
import type { DynamicTextProp } from '@magicschool/spellbook/hooks/useDynamicTextResolver';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { Children, isValidElement, useMemo, useRef } from 'react';

export type MultiSelectProps = Omit<SelectProps, 'defaultValue' | 'onValueChange' | 'value'> & {
  /** The default selected values. */
  defaultValue?: string[];
  /** Whether to show the default no results text when no options are found. Set to `true` if you want to handle the no results state yourself, for example when fetching options from an API. */
  disableDefaultNoResults?: boolean;
  /** Set `disableDefaultFiltering` to `true` to disable the default filtering behavior. This is useful when you want to implement your own filtering logic. */
  disableDefaultFiltering?: boolean;
  /** Set `disableTextHighlighting` to `true` to disable the text match highlighting in the options. */
  disableTextHighlighting?: boolean;
  /** The maximum number of chips to display before showing a "+X more" indicator. If not provided, all selected chips will be displayed. */
  filterChipVisibleLimit?: number;
  /** Handle the loading state of the options. When `isLoading` is true, a loading spinner is displayed in the dropdown. */
  isLoading?: boolean;
  /** A callback function triggered when the component loses focus. */
  onBlur?: (event: React.FocusEvent<HTMLButtonElement>) => void;
  /** A callback function triggered when the component receives focus. */
  onFocus?: (event: React.FocusEvent<HTMLButtonElement>) => void;
  /** To control behavior while typing a search string, you can use the `onInputChange` prop to handle search string changes and filter options accordingly. */
  onInputChange?: (searchString: string) => void;
  /** A callback function triggered when the value changes. It receives the new value as an argument. */
  onValueChange?: (value: string[]) => void;
  /** The controlled value of the select. */
  value?: string[];
  /** Configuration for component default text content, labels, placeholders, and screen reader announcements. */
  text?: {
    /** The accessible label for the dismiss button on each chip. Accepts a static string or a function that receives the chip label and returns a string for per-item screen reader announcements. */
    dismissibleLabel?: DynamicTextProp<[label: string]>;
    /** The text to display when there are more selected options than the visible limit. Accepts a static string or a function that receives the overflow count and returns a formatted string. */
    andMore?: DynamicTextProp<[count: number]>;
    /** The text to display when the clear all button is visible. */
    clearAll?: string;
    /** The default text to display when no results are found. */
    noResults?: string;
  };
};

export const MultiSelectPopover: React.FC<
  MultiSelectProps & {
    contentId: string;
    comboboxId: string;
  }
> = ({
  children,
  comboboxId,
  contentId,
  'data-testid': dataTestId,
  defaultOpen,
  descriptionOrientation,
  disabled,
  id,
  invalid,
  labelId,
  name: providedName,
  placeholder,
  readOnly,
  size,
  filterChipVisibleLimit,
  required,
  'aria-describedby': ariaDescribedBy,
  ...props
}) => {
  const name = providedName ? (providedName?.endsWith('[]') ? providedName : `${providedName}[]`) : undefined;
  const { slots } = useSlots(children, {
    trigger: {
      Component: MultiSelectTriggerSlot,
      props: {
        required,
        'aria-describedby': ariaDescribedBy,
        readOnly,
        id,
        labelId,
        disabled,
        comboboxId,
        contentId,
        name,
        placeholder,
        size,
        invalid,
        'data-testid': dataTestId,
        children,
      },
    },
    content: { Component: MultiSelectContentSlot, props: { children, disabled, size } },
    chips: { Component: MultiSelectChipsSlot, props: { filterChipVisibleLimit, id, disabled, readOnly } },
  });

  const { open, handleOpenChange, valueArray, clearSearchStringIfNoValueMatches, inputRef } = useMultiSelect();

  if (!slots.trigger) {
    return null;
  }

  return (
    <div data-spellbook-name="multi-select" className="flex flex-col items-center w-full sm:min-w-form-field-min">
      <Popover defaultOpen={defaultOpen} {...props} open={open} onOpenChange={handleOpenChange} {...props}>
        <Popover.Trigger>{slots.trigger}</Popover.Trigger>
        <Popover.Content
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onInteractOutside={clearSearchStringIfNoValueMatches}
          onFocusOutside={(event) => {
            // Selecting an option refocuses the trigger's input, which lives outside this
            // portaled content in the DOM. Radix treats that as focus leaving the layer and
            // dismisses it; ignore that specific case so the popover stays open across selections.
            if (event.target === inputRef.current) {
              event.preventDefault();
            }
          }}
          collisionPadding={10}
          // biome-ignore lint/a11y/useSemanticElements: composite multiselect popover
          role="listbox"
          {...(labelId && { 'aria-labelledby': labelId })}
          id={contentId}
          className="min-w-(--radix-popover-trigger-width) w-full"
        >
          {slots.content}
        </Popover.Content>
      </Popover>
      {slots.chips}
      {name && valueArray.length ? (
        valueArray.map((value) => <input key={value} type="hidden" name={name} value={value} id={`${id}-${value}`} />)
      ) : name && valueArray.length === 0 ? (
        <input type="hidden" name={name} value="" id={`${id}-empty`} />
      ) : null}
    </div>
  );
};

export const MultiSelectBase: React.FC<MultiSelectProps> = ({
  'data-testid': dataTestId,
  children,
  className,
  defaultOpen,
  disableDefaultFiltering,
  defaultValue,
  descriptionOrientation = 'horizontal',
  disabled = false,
  disableTextHighlighting,
  id: providedId,
  invalid = false,
  isLoading = false,
  labelId,
  name,
  onBlur,
  onFocus,
  onInputChange,
  onOpenChange,
  onValueChange,
  placeholder,
  readOnly = false,
  disableDefaultNoResults,
  size = 'md',
  value: providedValue,
  filterChipVisibleLimit,
  text = {},
  ...props
}) => {
  const id = useId(providedId);
  const comboboxId = `${id}-combobox`;
  const contentId = `${id}-content`;
  const inputRef = useRef<HTMLInputElement>(null);

  const initialOptions = useMemo(() => {
    const options = new Map<string, { label: string; value: string }>();

    Children.forEach(children, (child) => {
      if (!isValidElement(child)) {
        return;
      }

      if (child.type === MultiSelectOptionSlot) {
        const props = child.props as MultiSelectOptionProps;
        const { value, label, children: childContent } = props;
        const displayLabel = label || (typeof childContent === 'string' ? childContent : value);
        options.set(value, { value, label: displayLabel });
      } else if (child.type === MultiSelectGroupSlot) {
        const groupProps = child.props as MultiSelectGroupProps;
        if (groupProps.children) {
          Children.forEach(groupProps.children, (groupChild) => {
            if (isValidElement(groupChild) && groupChild.type === MultiSelectOptionSlot) {
              const optionProps = groupChild.props as MultiSelectOptionProps;
              const { value, label, children: childContent } = optionProps;
              const displayLabel = label || (typeof childContent === 'string' ? childContent : value);
              options.set(value, { value, label: displayLabel });
            }
          });
        }
      }
    });

    return options;
  }, [children]);

  return (
    <MultiSelectProvider
      disableDefaultFiltering={disableDefaultFiltering}
      defaultValue={defaultValue}
      descriptionOrientation={descriptionOrientation}
      disabled={disabled}
      disableTextHighlighting={disableTextHighlighting}
      initialOptions={initialOptions}
      inputRef={inputRef}
      isLoading={isLoading}
      text={text}
      onBlur={onBlur}
      onFocus={onFocus}
      onInputChange={onInputChange}
      onOpenChange={onOpenChange}
      onValueChange={onValueChange}
      readOnly={readOnly}
      disableDefaultNoResults={disableDefaultNoResults}
      value={providedValue}
    >
      <MultiSelectPopover
        comboboxId={comboboxId}
        contentId={contentId}
        data-testid={dataTestId}
        defaultOpen={defaultOpen}
        descriptionOrientation={descriptionOrientation}
        disabled={disabled}
        filterChipVisibleLimit={filterChipVisibleLimit}
        id={id}
        invalid={invalid}
        labelId={labelId}
        name={name}
        placeholder={placeholder}
        readOnly={readOnly}
        size={size}
        {...props}
      >
        {children}
      </MultiSelectPopover>
    </MultiSelectProvider>
  );
};

/**
 * A searchable dropdown for selecting multiple options, with selected items shown as dismissible chips.
 *
 * ### When to use
 * - Selecting multiple items from a list of 6+ options
 * - When vertical space is limited and CheckboxGroup would be too tall
 * - Tag/category selection, multi-subject pickers, filter selections
 * - When users need to search/filter within the options
 *
 * ### When NOT to use
 * - For 5 or fewer options where all can be visible: use `CheckboxGroup` instead
 * - For single selection: use `Select` or `Autocomplete` instead
 * - For yes/no or on/off toggles: use `Checkbox` or `Switch` instead
 *
 * See the [Select documentation](?path=/docs/components-select--docs) for a detailed comparison of selection components.
 *
 * ### API
 * - `value` / `onValueChange`: Controlled state (array of selected values)
 * - `defaultValue`: Uncontrolled initial selections (string array)
 * - `filterChipVisibleLimit`: Max visible chips before showing "+X more"
 * - `isLoading`: Shows spinner while fetching options
 * - `disableDefaultFiltering`: Disable built-in filter for server-side search
 * - `disableDefaultNoResults`: Hide default empty message for async states
 * - `text`: Nested localization config object — keys like `clearAll`, `dismissibleLabel`, and `andMore` live inside `text={{ ... }}`, not as top-level props
 *
 * ### Composition (slots)
 * - `<MultiSelect.Option>`: A selectable option. Pass `value` (required), `label`, optional `description`, `disabled`.
 * - `<MultiSelect.Group>`: Groups related options with a label header.
 * - `<MultiSelect.Chips>`: **Important**: Renders selected values as dismissible chips below the input. Include this slot for standard UX.
 * - `<MultiSelect.Empty>`: Custom empty/no-results message.
 * - `<MultiSelect.Spinner>`: Loading indicator (auto-shown when `isLoading={true}`).
 * - `<MultiSelect.LeadingAddOn>`: Content before field with border separator.
 * - `<MultiSelect.LeadingAdornment>`: Icon before the input text.
 * - `<MultiSelect.TrailingAddOn>`: Content after field with border separator.
 * - `<MultiSelect.TrailingAdornment>`: Icon after input (default: chevron).
 *
 * ### Chips behavior
 * - Always include `<MultiSelect.Chips />` unless managing selections elsewhere
 * - Use `filterChipVisibleLimit` to prevent overflow (e.g., `filterChipVisibleLimit={3}` shows "+2 more")
 * - Configure `text.dismissibleLabel` for accessible chip dismiss buttons
 * - Configure `text.andMore` for the overflow count format
 * - Configure `text.clearAll` for the clear all button text
 *
 * ### Visual states
 * - **Default**: Standard border, chevron icon
 * - **Focused/Open**: Focus ring, dropdown visible
 * - **With selections**: Chips displayed below input, checkmarks on selected options
 * - **Loading**: Spinner in dropdown
 * - **Invalid**: Error border color
 * - **Disabled**: Muted background, chips not dismissible
 *
 * ### Accessibility
 * - Uses listbox pattern with multi-selection
 * - Each chip has accessible dismiss button with `text.dismissibleLabel`
 * - Arrow keys navigate options, Space toggles selection
 * - The dropdown stays open across selections so users can pick multiple options without reopening it — it only closes on blur, `Escape`, `Tab`, or clicking outside
 *
 * ### Label association
 * This is an atomic component decoupled from labels. Associate a label using one of:
 * - `labelId`: Pass the ID of an external `<Label>` element
 * - `aria-label`: For visually hidden labels
 * - `id` + `<Label htmlFor={id}>`: Standard HTML label association
 *
 * ### Form integration
 * - Pass `name` prop to render hidden inputs for form submission
 * - Values are submitted as `name[]` array format
 * - Empty selection submits empty string to indicate "no selection"
 */
export const MultiSelect = Object.assign(MultiSelectBase, {
  Chips: MultiSelectChipsSlot,
  Content: MultiSelectContentSlot,
  Empty: MultiSelectEmptySlot,
  Group: MultiSelectGroupSlot,
  LeadingAddOn: MultiSelectLeadingAddOnSlot,
  LeadingAdornment: MultiSelectLeadingAdornmentSlot,
  Option: MultiSelectOptionSlot,
  Spinner: MultiSelectSpinnerSlot,
  TrailingAddOn: MultiSelectTrailingAddOnSlot,
  TrailingAdornment: MultiSelectTrailingAdornmentSlot,
  Trigger: MultiSelectTriggerSlot,
});
